/**
 * The gap between two rows of the board is a channel (#78): every line through it comes down from the row above at one
 * place across and goes on into the row below at another. Where those differ the line turns across the gap on a track
 * of its own, and the tracks are stacked so that two lines meet only where they must: where their ends come in one order
 * above and the other below.
 *
 * A line coming down from above at a place within another's run would cross that run, so it lies above it; a line going
 * on down at a place within another's run would cross it, so that run lies above it. Between two lines turning the same
 * way those rules never disagree unless their ends swap over: lines turning right stack with the one coming down
 * furthest right on top (of two out of one place, the one going furthest), lines turning left the other way round. A
 * line turning right and one turning left meet only where their ends swap over, and they are merged into one stack
 * (each keeping its own side's order) so as to break fewest rules, found for every way of merging the two (a shortest
 * way through a grid). Every rule broken is one crossing, and two lines whose ends swap over break one rule each way:
 * so the lines in a channel cross exactly as often as their ends swap over. Lines leaving one place (a fork) or coming
 * into one (a merge) share it, and out to opposite sides may share a track.
 */

export interface ChannelRun {
  id: string;
  /** Where across it comes down from the row above, and what it comes from there (a step, or a lane). */
  top: number;
  from: string;
  /** Where across it goes on into the row below, and what it goes into there. */
  bottom: number;
  to: string;
}

const lo = (r: ChannelRun) => Math.min(r.top, r.bottom);
const hi = (r: ChannelRun) => Math.max(r.top, r.bottom);
const at = (x: number, r: ChannelRun) => x >= lo(r) - 0.5 && x <= hi(r) + 0.5;
/** Two runs out of one place, or into one. */
const forked = (a: ChannelRun, b: ChannelRun) => a.from === b.from && Math.abs(a.top - b.top) < 0.5;
const merged = (a: ChannelRun, b: ChannelRun) => a.to === b.to && Math.abs(a.bottom - b.bottom) < 0.5;

/** How many rules say `a` lies above `b`: `a` comes down within `b`'s run, or `b` goes on down within `a`'s. */
export function above(a: ChannelRun, b: ChannelRun): number {
  let n = 0;
  if (!forked(a, b) && at(a.top, b)) n++;
  if (!merged(a, b) && at(b.bottom, a)) n++;
  return n;
}

/** Whether two runs may lie on one track: apart across, or out of one place (or into one) to opposite sides. */
function share(a: ChannelRun, b: ChannelRun, room: number): boolean {
  if (hi(a) + room <= lo(b) || hi(b) + room <= lo(a)) return true;
  const sides = Math.sign(a.bottom - a.top) !== Math.sign(b.bottom - b.top);
  return sides && ((forked(a, b) && !merged(a, b)) || (merged(a, b) && !forked(a, b)));
}

/**
 * Each run's track in its channel, from the top (0), and how many tracks the channel takes. `room` is how far apart
 * across two runs on one track keep.
 */
export function channelTracks(runs: readonly ChannelRun[], room: number): { track: Map<string, number>; tracks: number } {
  const right = runs.filter((r) => r.bottom > r.top).sort((a, b) => b.top - a.top || b.bottom - a.bottom || (a.id < b.id ? -1 : 1));
  const left = runs.filter((r) => r.bottom < r.top).sort((a, b) => a.top - b.top || a.bottom - b.bottom || (a.id < b.id ? -1 : 1));
  // Merged: best[i][j], the fewest rules broken with the first i turning right and the first j turning left stacked.
  // Putting right[i] next, below left[0..j) and above the rest of the left: it breaks every rule that it lies above one
  // of those already placed, and every rule that one of those still to come lies above it.
  const cost = right.map((r) => {
    const out = Array<number>(left.length + 1).fill(0);
    for (let j = 0; j <= left.length; j++) {
      let n = 0;
      for (let k = 0; k < left.length; k++) n += k < j ? above(r, left[k]!) : above(left[k]!, r);
      out[j] = n;
    }
    return out;
  });
  const best: number[][] = Array.from({ length: right.length + 1 }, () => Array<number>(left.length + 1).fill(Infinity));
  const came: ('right' | 'left')[][] = Array.from({ length: right.length + 1 }, () => Array<'right' | 'left'>(left.length + 1).fill('left'));
  best[0]![0] = 0;
  for (let i = 0; i <= right.length; i++)
    for (let j = 0; j <= left.length; j++) {
      if (i > 0 && best[i - 1]![j]! + cost[i - 1]![j]! < best[i]![j]!) {
        best[i]![j] = best[i - 1]![j]! + cost[i - 1]![j]!;
        came[i]![j] = 'right';
      }
      if (j > 0 && best[i]![j - 1]! < best[i]![j]!) {
        best[i]![j] = best[i]![j - 1]!;
        came[i]![j] = 'left';
      }
    }
  const order: ChannelRun[] = [];
  for (let i = right.length, j = left.length; i > 0 || j > 0; ) {
    if (came[i]![j] === 'right') order.unshift(right[--i]!);
    else order.unshift(left[--j]!);
  }
  // Each on the first track below every run before it that it may not share a track with.
  const track = new Map<string, number>();
  let tracks = 0;
  order.forEach((r, i) => {
    const t = Math.max(-1, ...order.slice(0, i).filter((q) => !share(q, r, room)).map((q) => track.get(q.id)!)) + 1;
    track.set(r.id, t);
    tracks = Math.max(tracks, t + 1);
  });
  return { track, tracks };
}
